
/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { Injectable, inject } from "@angular/core";
import { Router, Routes } from "@angular/router";

import { User } from "@src/app/model/user";
import { AgentDashboardPageComponent } from "@src/app/modules/agent/agent-dashboard-page/agent-dashboard-page.component";
import { OmcPageComponent } from "@src/app/modules/agent/omc-page/omc-page.component";
import { BillingPageComponent } from "@src/app/modules/budget/billing-page/billing-page.component";
import { BudgetingPageComponent } from "@src/app/modules/budget/budgeting-page/budgeting-page.component";
import { CyclePageComponent } from "@src/app/modules/budget/cycle-page/cycle-page.component";
import { DocumentPageComponent } from "@src/app/modules/crm/document-page/document-page.component";
import { NewUserRequestComponent } from "@src/app/modules/crm/new-user-request/new-user-request.component";
import { PersonalRequestsComponent } from "@src/app/modules/crm/personal-requests/personal-requests.component";
import { RequestCategoriesPageComponent } from "@src/app/modules/crm/request-categories-page/request-categories-page.component";
import { ImportPageComponent } from "@src/app/modules/importer/import-page/import-page.component";
import { BallotPageComponent } from "@src/app/modules/social/ballot-page/ballot-page.component";
import { ForumComponent } from "@src/app/modules/social/forum/forum.component";
import { PostListComponent } from "@src/app/modules/social/post/post-list/post-list.component";
import { QuestionPageComponent } from "@src/app/modules/social/question-page/question-page.component";
import { BankAccountPageComponent } from "@src/app/modules/txn/bank-account-page/bank-account-page.component";
import { ConfirmPageComponent } from "@src/app/modules/txn/payments/confirm-page/confirm-page.component";
import { PaymentOptionsComponent } from "@src/app/modules/txn/payments/payment-options/payment-options.component";
import { PurchaseUploadsPageComponent } from "@src/app/modules/txn/purchase-uploads-page/purchase-uploads-page.component";
import { AreaPageComponent } from "@src/app/modules/unit/area/area-page.component";
import { UnitTypePageComponent } from "@src/app/modules/unit/unit-type-page/unit-type-page.component";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { LoginComponent } from "@src/app/modules/user/login/login.component";
import { RegisterUserComponent } from "@src/app/modules/user/register/register.component";
import { UserProfileComponent } from "@src/app/modules/user/user-profile/user-profile.component";
import { HomePageComponentComponent } from "@src/app/pages/home-page-component/home-page-component.component";
import { OAuthCallbackComponent } from "@src/app/pages/o-auth-callback/o-auth-callback.component";
import { RolePageComponent } from "@src/app/pages/role-page/role-page.component";
import { NavRoute } from "@src/app/shared/NavRoute";
import { SearchPageComponent } from "@src/app/pages/search-page/search-page.component";
import { TermsOfServiceComponent } from "@src/app/shared/legal/terms-of-service/terms-of-service.component";
import { JournalPageComponent } from "@src/app/modules/txn/journal-page/journal-page.component";
import { OmcSignUpComponent } from "@src/app/modules/user/omc-sign-up/omc-sign-up.component";
import { TrialBalanceComponent } from "@src/app/modules/txn/balance/trial-balance/trial-balance.component";
import { IncomeExpenditureComponent } from "@src/app/modules/txn/balance/income-expenditure/income-expenditure.component";
import { BalanceSheetComponent } from "@src/app/modules/txn/balance/balance-sheet/balance-sheet.component";
import { BankReconciliationComponent } from "@src/app/modules/txn/bank-reconciliation/bank-reconciliation.component";
import { FixedAssetsPageComponent } from "@src/app/modules/preventative/fixed-assets-page/fixed-assets-page.component";
import { FixedAssetTypesPageComponent } from "@src/app/modules/preventative/fixed-asset-types-page/fixed-asset-types-page.component";
import { ImageUploadRequestComponent } from "@src/app/modules/crm/service-request-page/image-upload-request/image-upload-request.component";
import { OmcSettingsComponent } from "@src/app/pages/omc-settings/omc-settings.component";
import { ResidentsHomePageComponent } from "@src/app/modules/public/residents/residents.component";
import { OwnersHomePageComponent } from "@src/app/modules/public/owners/owners.component";
import { DirectorsHomePageComponent } from "@src/app/modules/public/directors/directors.component";
import { AgentsHomePageComponent } from "@src/app/modules/public/agents/agents.component";
import { AccountantsHomePageComponent } from "@src/app/modules/public/accountants/accountants.component";
import { ContractorsHomePageComponent } from "@src/app/modules/public/contractors/contractors.component";
import { PricingComponent } from "@src/app/modules/public/pricing/pricing.component";
import { JoinAnOmcComponent } from "@src/app/modules/user/join-an-omc/join-an-omc.component";
import { DemosComponent } from "@src/app/modules/public/demos/demos.component";
import { AboutUsComponent } from "@src/app/modules/public/about-us/about-us.component";
import { ContactUsComponent } from "@src/app/modules/public/contact-us/contact-us.component";
import { MyUnitPageComponent } from "@src/app/modules/unit/my-unit-page/my-unit-page.component";
import { CommsTemplatePageNavRoute } from "@src/app/routes/comms-template-page-nav";
import { MailMergePageComponent } from "@src/app/modules/agent/mail-merge-page/mail-merge-page.component";
import { AgentSettingsComponent } from "@src/app/pages/agent-settings/agent-settings.component";
import { PrivacyPolicyComponent } from "@src/app/shared/legal/privacy-policy/privacy-policy.component";
import { DataProcessingAgreementComponent } from "@src/app/shared/legal/data-processing-agreement/data-processing-agreement.component";
import { CanDeactivateGuard } from "@src/app/shared/guards/can-deactivate.guard";
import { LoggedInGuard } from "@src/app/shared/guards/loggedin.guard";
import { DashboardHolderComponent } from "@src/app/pages/dashboard-holder/dashboard-holder.component";
import { BCodePageNavRoute } from "@src/app/routes/bcode-page-nav";
import { BillingDetailComponent } from "@src/app/modules/budget/billing-page/billing-detail/billing-detail.component";
import { SchedulePageNavRoute } from "@src/app/routes/schedule-page-nav";
import { UnitPageNavRoute } from "@src/app/routes/unit-page";
import { ActionPageNavRoute } from "@src/app/routes/action-page-nav";
import { DocumentDetailPageComponent } from "@src/app/modules/crm/document-detail-page/document-detail-page.component";
import { MyRequestComponent } from "@src/app/modules/crm/my-request/my-request.component";
import { RecurringJobPageNavRoute } from "@src/app/routes/recurring-job-page-nav";
import { PreferredSupplierPageNavRoute } from "@src/app/routes/preferred-supplier-page-nav";
import { PostItemComponent } from "@src/app/modules/social/post/post-item/post-item.component";
import { InvalidNavigationComponent } from "@src/app/shared/invalid-navigation/invalid-navigation.component";
import { SupplierPageNavRoute } from "@src/app/routes/supplier-page-nav";
import { ApDetailPageNavRoute } from "@src/app/routes/ap-detail-page.nav";
import { PersonPageNavRoute } from "@src/app/routes/person-page-nav";
import { FieldLabNavRoute } from "@src/app/routes/field-lab-nav";
import { TeamUserPageNavRoute } from "@src/app/routes/team-user-page-nav";
import { TxnPageNavRoute } from "@src/app/routes/txn-page-nav";
import { ArPageNavRoute } from "@src/app/routes/ar-page-nav";
import { InvoicePageNavRoute } from "@src/app/routes/invoice-page-nav";
import { DebtorsListPageNavRoute } from "@src/app/routes/debtors-list-page-nav";
import { DebtorsAnalysisNavRoute } from "@src/app/routes/debtors-analysis-page.nav";
import { CreditorsListPageNavRoute } from "@src/app/routes/creditors-list-page";
import { ListComponent } from "@src/app/modules/txn/bank-reconciliation/list/list.component";
import { CreditNotePageNavRoute } from "@src/app/routes/credit-note-page.nav";
import { PurchasePageNavRoute } from "@src/app/routes/purchase-page-nav";
import { Txn } from "@src/app/model/txn";
import { BankPageNavRoute } from "@src/app/routes/bank-page-nav";
import { BankInPageNavRoute } from "@src/app/routes/bank-in-page-nav";
import { BankOutPageNavRoute } from "@src/app/routes/bank-out-page-nav";
import { ApPageNavRoute } from "@src/app/routes/ap-page-nav";
import { BallotEditorComponent } from "@src/app/modules/social/ballot-editor/ballot-editor.component";
import { ServiceRequestPageNavRoute } from "@src/app/routes/service-request-page-nav";
import { RequestTemplatePageNavRoute } from "@src/app/routes/request-template-page-nav";
import { AiLogPageNavRoute } from "@src/app/routes/ai-log-page-nav";
import { OmcPageNavRoute } from "@src/app/routes/omc-page-nav";
import { RequestAnalysisPageNavRoute } from "@src/app/routes/request-analysis-page-nav";
import { RequestAnalysisByUserPageNavRoute } from "@src/app/routes/request-analysis-by-user-page-nav";
import { VatReturnPageComponent } from "@src/app/modules/txn/vat-return/vat-return-page.component";
import { AgentContactPageNavRoute } from "@src/app/routes/agent-contact-page-nav";

export interface AppRouteAble {
    readonly navRoute: NavRoute;
}

export const AgentDashboardNavRoute = new NavRoute('agent/dashboard', ()=>AgentDashboardPageComponent, 'grid_view');
export const MailMergeNavRoute = new NavRoute('mailMerge', ()=>MailMergePageComponent, 'merge');
export const BillingPageNavRoute = new NavRoute('budgets/billing', ()=>BillingPageComponent, 'receipt_long', ()=>BillingDetailComponent);
export const BudgetingPageNavRoute = new NavRoute('budgets/budgeting', ()=>BudgetingPageComponent, 'savings');
export const CyclePageNavRoute = new NavRoute('budgets/cycles', ()=>CyclePageComponent, 'autorenew');
export const DocumentPageNavRoute = new NavRoute('crm/documents', ()=>DocumentPageComponent, 'library_books',()=>DocumentDetailPageComponent)
export const NewUserRequestNavRoute = new NavRoute('newUserRequest', ()=>NewUserRequestComponent, 'live_help')
export const PersonalRequestPageNavRoute = new NavRoute('myRequests', ()=>PersonalRequestsComponent, 'contact_support',()=>MyRequestComponent);
export const QuestionPageNavRoute = new NavRoute('social/questions', ()=>QuestionPageComponent, 'question_answer');
export const PostPageNavRoute = new NavRoute('social/posts', ()=>PostListComponent, 'forum',()=>PostItemComponent);
export const ForumPageNavRoute = new NavRoute('social/channels', ()=>ForumComponent, 'diversity_3');
export const SearchPageNavRoute = new NavRoute('search', ()=>SearchPageComponent, 'search')
export const UserProfileNavRoute = new NavRoute('my/profile', ()=>UserProfileComponent, 'contact_page');
export const LoginPageNavRoute = new NavRoute('login', ()=>LoginComponent, 'login');
export const RegisterUserNavRoute = new NavRoute('register/:token', ()=>RegisterUserComponent, '');
export const OmcSignUpPageNavRoute = new NavRoute('omcSignUp', ()=>OmcSignUpComponent, '');
export const TrialBalancePageNavRoute = new NavRoute('txn/trialBalance', ()=>TrialBalanceComponent, 'balance');
export const IncomeExpenditurePageNavRoute = new NavRoute('txn/incomeExpenditure', ()=>IncomeExpenditureComponent, 'finance');
export const BalanceSheetPageNavRoute = new NavRoute('txn/balanceSheet', ()=>BalanceSheetComponent, 'rubric')
export const FixedAssetPageNavRoute = new NavRoute('fassets', ()=>FixedAssetsPageComponent, 'inventory');
export const FixedAssetTypePageNavRoute = new NavRoute('fassetTypes', ()=>FixedAssetTypesPageComponent, 'inventory');
export const BankRecPageNavRoute = new NavRoute('txn/bankRec', ()=>ListComponent, 'balance').setItemComponent(()=>BankReconciliationComponent)
export const PurchaseUploadPageNavRoute = new NavRoute(Txn.TYPE.PURCHASE.code + '/uploads', ()=>PurchaseUploadsPageComponent, 'cloud_upload');
export const ConfirmPageNavRoute = new NavRoute('payments/:sessionId', ()=>ConfirmPageComponent, '');
export const PaymentOptionsNavRoute = new NavRoute('payments', ()=>PaymentOptionsComponent, '');
export const BankAccountPageNavRoute = new NavRoute('bank-accounts', ()=>BankAccountPageComponent, 'account_balance');
export const JournalPageNavRoute = new NavRoute(Txn.TYPE.JRNL.code, ()=>JournalPageComponent, 'instant_mix');
export const BallotPageNavRoute = new NavRoute('social/ballots', ()=>BallotPageComponent, 'ballot',()=>BallotEditorComponent)
export const UnitTypePageNavRoute = new NavRoute('settings/unit-types', ()=>UnitTypePageComponent, 'workspaces');
export const RolePageNavRoute = new NavRoute('settings/roles', ()=>RolePageComponent, 'groups');
export const AreaPageNavRoute = new NavRoute('settings/areas', ()=>AreaPageComponent, 'area_chart');
export const RequestCategoriesPageNavRoute = new NavRoute('crm/categories', ()=>RequestCategoriesPageComponent, 'category');
export const MyUnitPageNavRoute = new NavRoute('myunits', ()=>MyUnitPageComponent, 'account_balance');
export const ImportPageNavRoute = new NavRoute('imports', ()=>ImportPageComponent, 'publish');
export const OmcSettingsPageNavRoute = new NavRoute('omcSettings', ()=>OmcSettingsComponent, 'settings_applications');
export const AgentSettingsPageNavRoute = new NavRoute('agentSettings', ()=>AgentSettingsComponent, 'settings_applications');
export const ImageUploadPageNavRoute = new NavRoute('crm/quickRequest', () => ImageUploadRequestComponent, 'live_help');
export const VatReturnPageNavRoute = new NavRoute('vatReturn', () => VatReturnPageComponent, 'live_help');

export const appRoutes: Routes = [
    {
        path: 'termsOfService',
        component: TermsOfServiceComponent,
    },
    {
        path: 'privacyPolicy',
        component: PrivacyPolicyComponent,
    },
    {
        path: 'dataProcessingAgreement',
        component: DataProcessingAgreementComponent,
    },    
    AgentDashboardNavRoute.listRoute,
    {
        path: OmcPageNavRoute.url,
        component: OmcPageComponent,
        canDeactivate: [CanDeactivateGuard],
        canActivate: [LoggedInGuard],
        pathMatch: 'full',
        data: { animation: 'listRoute', agentLandingPage: true }
    },
    OmcPageNavRoute.itemRoute,

    MailMergeNavRoute.listRoute,
    MailMergeNavRoute.itemRoute,

    SearchPageNavRoute.listRoute,
    UserProfileNavRoute.openRoute, // not really open, will divert if currentUser not retrieved.

    BCodePageNavRoute.listRoute,
    BCodePageNavRoute.itemRoute,

    LoginPageNavRoute.openRoute,
    NewUserRequestNavRoute.openRoute,
    PersonalRequestPageNavRoute.listRoute, // In case not logged in... openRoute,
    PersonalRequestPageNavRoute.itemRoute, // so not open

    RegisterUserNavRoute.openRoute,
    OmcSignUpPageNavRoute.openRoute,

    PersonPageNavRoute.listRoute,
    PersonPageNavRoute.itemRoute,

    AgentContactPageNavRoute.listRoute,
    AgentContactPageNavRoute.itemRoute,

    TeamUserPageNavRoute.listRoute,
    TeamUserPageNavRoute.newRoute,
    TeamUserPageNavRoute.itemRoute,

    TxnPageNavRoute.listRoute,
    TrialBalancePageNavRoute.listRoute,
    TrialBalancePageNavRoute.openItemRoute,
    IncomeExpenditurePageNavRoute.listRoute,
    BalanceSheetPageNavRoute.listRoute,

    ArPageNavRoute.listRoute,

    FixedAssetPageNavRoute.listRoute,
    FixedAssetPageNavRoute.itemRoute,

    FixedAssetTypePageNavRoute.listRoute,
    FixedAssetTypePageNavRoute.itemRoute,

    InvoicePageNavRoute.listRoute,
    InvoicePageNavRoute.itemRoute,

    DebtorsListPageNavRoute.listRoute,
    DebtorsAnalysisNavRoute.listRoute,
    CreditorsListPageNavRoute.listRoute,

    BankRecPageNavRoute.listRoute,
    BankRecPageNavRoute.itemRoute,

    CreditNotePageNavRoute.listRoute,
    CreditNotePageNavRoute.itemRoute,

    PurchasePageNavRoute.listRoute,
    PurchaseUploadPageNavRoute.listRoute,
    PurchasePageNavRoute.itemRoute,

    ConfirmPageNavRoute.openRoute,

    PaymentOptionsNavRoute.openRoute,

    BankPageNavRoute.listRoute,
    BankPageNavRoute.itemRoute,

    BankAccountPageNavRoute.listRoute,
    BankAccountPageNavRoute.itemRoute,

    PreferredSupplierPageNavRoute.listRoute,
    PreferredSupplierPageNavRoute.itemRoute,

    SupplierPageNavRoute.listRoute,
    SupplierPageNavRoute.itemRoute,

    BankInPageNavRoute.listRoute,
    BankInPageNavRoute.itemRoute,

    BankOutPageNavRoute.listRoute,
    BankOutPageNavRoute.itemRoute,

    ApPageNavRoute.listRoute,
    ApPageNavRoute.itemRoute,
    ApDetailPageNavRoute.listRoute,

    TxnPageNavRoute.itemRoute,

    JournalPageNavRoute.listRoute,
    JournalPageNavRoute.itemRoute,

    { path: 'oAuthCallback', component: OAuthCallbackComponent },

    PostPageNavRoute.listRoute, // is an item view, but avoiding duplicate path...
    PostPageNavRoute.itemRoute,

    ForumPageNavRoute.listRoute,
    ForumPageNavRoute.itemRoute,

    BallotPageNavRoute.listRoute,
    BallotPageNavRoute.itemRoute,

    QuestionPageNavRoute.listRoute,
    QuestionPageNavRoute.itemRoute,


    RecurringJobPageNavRoute.itemRoute,
    RecurringJobPageNavRoute.listRoute,

    ServiceRequestPageNavRoute.listRoute,
    ServiceRequestPageNavRoute.itemRoute,
    ImageUploadPageNavRoute.listRoute,

    RequestAnalysisPageNavRoute.listRoute,
    RequestAnalysisByUserPageNavRoute.listRoute,

    ActionPageNavRoute.listRoute,
    ActionPageNavRoute.itemRoute,

    UnitTypePageNavRoute.listRoute,
    UnitTypePageNavRoute.itemRoute,

    RolePageNavRoute.listRoute,
    RolePageNavRoute.itemRoute,

    AreaPageNavRoute.listRoute,
    AreaPageNavRoute.itemRoute,

    RequestTemplatePageNavRoute.itemRoute,
    RequestTemplatePageNavRoute.listRoute,
    AiLogPageNavRoute.itemRoute,
    AiLogPageNavRoute.listRoute,

    CommsTemplatePageNavRoute.itemRoute,
    CommsTemplatePageNavRoute.listRoute,

    RequestCategoriesPageNavRoute.itemRoute,
    RequestCategoriesPageNavRoute.listRoute,

    SchedulePageNavRoute.itemRoute,
    SchedulePageNavRoute.listRoute,

    CyclePageNavRoute.itemRoute,
    CyclePageNavRoute.listRoute,

    BillingPageNavRoute.itemRoute,
    BillingPageNavRoute.listRoute,

    BudgetingPageNavRoute.itemRoute,
    BudgetingPageNavRoute.listRoute,


    DocumentPageNavRoute.listRoute,
    DocumentPageNavRoute.itemRoute,

    UnitPageNavRoute.itemRoute,
    UnitPageNavRoute.listRoute,

    MyUnitPageNavRoute.listRoute,
    MyUnitPageNavRoute.itemRoute,

    ImportPageNavRoute.listRoute,
    ImportPageNavRoute.itemRoute,

    VatReturnPageNavRoute.listRoute,
    VatReturnPageNavRoute.itemRoute,

    FieldLabNavRoute.listRoute,
    FieldLabNavRoute.itemRoute,

    //HomePageComponentComponent.navRoute.openRoute,
    { path: ':tenantId/home', component: DashboardHolderComponent, 
        data: {animation: 'HomePage'},
        canActivate: [LoggedInGuard]
    },
    { path: '', component: HomePageComponentComponent, data: {animation: 'HomePage'}},
    { path: 'pricing', component: PricingComponent, data: {animation: 'PricingPage'}},
    { path: 'residents', component: ResidentsHomePageComponent, data: {animation: 1} },
    { path: 'owners', component: OwnersHomePageComponent, data: {animation: 2 }},
    { path: 'directors', component: DirectorsHomePageComponent, data: {animation: 3 }},
    { path: 'agents', component: AgentsHomePageComponent, data: {animation: 4 }},
    { path: 'accountants', component: AccountantsHomePageComponent, data: {animation: 5 }},
    { path: 'contractors', component: ContractorsHomePageComponent, data: {animation: 6 }},
    { path: 'demos', component: DemosComponent, data: {animation: 'demos'}},
    { path: 'about-us', component: AboutUsComponent, data: {animation: 'about-us'}},
    { path: 'contact-us', component: ContactUsComponent, data: {animation: 'contact-us'}},

    {path: 'unknownUser', component: JoinAnOmcComponent},

    OmcSettingsPageNavRoute.listRoute,
    AgentSettingsPageNavRoute.listRoute,

    { path: '**', component: InvalidNavigationComponent },
];


@Injectable({
    providedIn: 'root'
})
export class AppRouter {
    private currentUserSvc = inject(CurrentUserService);
    private router = inject(Router);


    currentUser: User;

    constructor() {
        const currentUserSvc = this.currentUserSvc;

        currentUserSvc.getCurrentUser().subscribe(u => {
            this.currentUser = u;
        });
    }

}
